import React, { useState, useEffect } from 'react';
import { X, Search, Eye, Heart, MessageCircle, Share2, Calendar, Check } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { integrationsAPI } from '../services/integrationsAPI';
import { userAPI, familyAPI } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import { TextInput } from './ui';

interface YouTubeVideo {
  id: string;
  provider_post_id: string;
  title: string;
  description?: string;
  thumbnail_url?: string;
  video_url: string;
  published_at: string;
  view_count: number;
  like_count: number;
  comment_count: number;
}

interface YouTubeVideoPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectVideos: (videoLinks: string[]) => void;
  title?: string;
  maxSelections?: number;
}

const YouTubeVideoPickerModal: React.FC<YouTubeVideoPickerModalProps> = ({
  isOpen,
  onClose,
  onSelectVideos,
  title = 'Select YouTube Videos',
  maxSelections = 10,
}) => {
  const { sessionToken } = useAuth();
  const { showToast } = useToast();
  const { isMobile } = useResponsive();
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [athleteId, setAthleteId] = useState<string>('');
  const [connectionStatus, setConnectionStatus] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredVideos, setFilteredVideos] = useState<YouTubeVideo[]>([]);

  // Load athlete data and YouTube connection status
  useEffect(() => {
    if (isOpen && sessionToken) {
      loadAthleteData();
    }
  }, [isOpen, sessionToken]);

  // Filter videos based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredVideos(videos);
    } else {
      const filtered = videos.filter(video =>
        video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        video.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredVideos(filtered);
    }
  }, [searchQuery, videos]);

  const loadAthleteData = async () => {
    try {
      setIsLoading(true);
      
      const userProfile = await userAPI.getProfile(sessionToken!);
      const familyResult = await familyAPI.getMyFamily(sessionToken!);
      
      if (familyResult.family?.members) {
        const familyMember = familyResult.family.members.find(
          (member: any) => member.id === userProfile.id
        );
        
        if (familyMember?.athlete_profile_id) {
          const currentAthleteId = familyMember.athlete_profile_id;
          setAthleteId(currentAthleteId);
          await checkYouTubeConnectionAndLoadVideos(currentAthleteId);
        }
      }
    } catch (error) {
      console.error('Failed to load athlete data:', error);
      setIsLoading(false);
    }
  };

  const checkYouTubeConnectionAndLoadVideos = async (userId: string) => {
    try {
      const status = await integrationsAPI.getConnectionStatus(userId);
      setConnectionStatus(status.youtube);
      
      if (status.youtube) {
        await loadYouTubeVideos(userId);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error checking YouTube connection:', error);
      setConnectionStatus(false);
      setIsLoading(false);
    }
  };

  const loadYouTubeVideos = async (userId: string) => {
    try {
      const analytics = await integrationsAPI.getAnalytics(userId);
      
      const youtubeVideos: YouTubeVideo[] = [];
      
      if (analytics.data?.posts && Array.isArray(analytics.data.posts)) {
        analytics.data.posts.forEach((post: any) => {
          if (post.provider === 'youtube' && post.provider_post_id) {
            youtubeVideos.push({
              id: post.id?.toString() || post.provider_post_id,
              provider_post_id: post.provider_post_id,
              title: post.title || 'Untitled Video',
              description: post.description || post.content_text,
              thumbnail_url: post.thumbnail_url,
              video_url: post.video_url || `https://www.youtube.com/watch?v=${post.provider_post_id}`,
              published_at: post.published_at || post.created_at,
              view_count: post.view_count || 0,
              like_count: post.like_count || 0,
              comment_count: post.comment_count || 0,
            });
          }
        });
      }
      
      // Sort by newest first
      youtubeVideos.sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime());
      
      setVideos(youtubeVideos);
      setFilteredVideos(youtubeVideos);
    } catch (error) {
      console.error('Error loading YouTube videos:', error);
    }
  };

  const handleVideoToggle = (videoId: string) => {
    const newSelected = new Set(selectedVideos);
    
    if (newSelected.has(videoId)) {
      newSelected.delete(videoId);
    } else {
      if (newSelected.size >= maxSelections) {
        showToast(`You can only select up to ${maxSelections} videos.`, 'warning');
        return;
      }
      newSelected.add(videoId);
    }
    
    setSelectedVideos(newSelected);
  };

  const handleConfirmSelection = () => {
    const selectedVideoData = videos.filter(video => selectedVideos.has(video.id));
    const videoLinks = selectedVideoData.map(video => video.video_url);
    
    onSelectVideos(videoLinks);
    handleClose();
  };

  const handleClose = () => {
    setSelectedVideos(new Set());
    setSearchQuery('');
    onClose();
  };

  const formatViews = (views: number) => {
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M`;
    } else if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}K`;
    }
    return views.toString();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1003,
        padding: isMobile ? '16px' : '20px',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '900px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          background: '#F9FAFB',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: 'white',
            padding: isMobile ? '16px' : '20px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #E5E7EB',
            flexShrink: 0,
          }}
        >
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#374151',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={24} />
          </button>
          
          <h2
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#1F2937',
            }}
          >
            {title}
          </h2>
          
          {selectedVideos.size > 0 ? (
            <button
              onClick={handleConfirmSelection}
              style={{
                padding: '8px 16px',
                background: '#FF0000',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Add ({selectedVideos.size})
            </button>
          ) : (
            <div style={{ width: '80px' }} />
          )}
        </div>

        {/* Content */}
        {isLoading ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #FEE2E2',
                borderTop: '4px solid #FF0000',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }} />
              <style>
                {`
                  @keyframes spin {
                    to { transform: rotate(360deg); }
                  }
                `}
              </style>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#1F2937'
              }}>
                Loading your YouTube videos...
              </div>
            </div>
          </div>
        ) : !connectionStatus ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px',
          }}>
            <div style={{ textAlign: 'center', maxWidth: '400px' }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#FF0000" strokeWidth="2" style={{ margin: '0 auto 24px' }}>
                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
                <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
              </svg>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 12px'
              }}>
                YouTube Not Connected
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#6B7280',
                lineHeight: '1.5',
                margin: 0
              }}>
                Connect your YouTube account to access your videos for sharing in emails and posts.
              </p>
            </div>
          </div>
        ) : videos.length === 0 ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px',
          }}>
            <div style={{ textAlign: 'center', maxWidth: '400px' }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="2" style={{ margin: '0 auto 24px' }}>
                <polygon points="23 7 16 12 23 17 23 7"></polygon>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 12px'
              }}>
                No Videos Found
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#6B7280',
                lineHeight: '1.5',
                margin: 0
              }}>
                Upload some videos to YouTube first, then they'll appear here for sharing.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Search Bar */}
            <div style={{
              padding: '20px 24px',
              background: 'white',
              borderBottom: '1px solid #E5E7EB',
            }}>
              {/* The search chrome used to be a bordered div wrapping a
                * borderless input that set `outline: none`, so the box looked
                * like a field and drew nothing on keyboard focus. TextInput's
                * leftIcon draws the same magnifier-plus-box and keeps the ring;
                * the clear button rides on top at the right, which is the one
                * thing the primitive has no slot for. */}
              <div style={{ position: 'relative' }}>
                <TextInput
                  type="text"
                  placeholder="Search your videos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search your videos"
                  leftIcon={<Search size={20} />}
                  style={{ paddingRight: 44 }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                    style={{
                      position: 'absolute',
                      right: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--aps-text-subtle)',
                    }}
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            </div>

            {/* Selection Info */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 24px',
              background: 'white',
              borderBottom: '1px solid #E5E7EB',
            }}>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>
                {filteredVideos.length} video{filteredVideos.length !== 1 ? 's' : ''}
                {searchQuery && ' found'}
              </div>
              <div style={{ fontSize: '14px', color: '#FF0000', fontWeight: '500' }}>
                {selectedVideos.size}/{maxSelections} selected
              </div>
            </div>

            {/* Videos List */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px',
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                gap: '24px',
              }}>
                {filteredVideos.map((video) => {
                  const isSelected = selectedVideos.has(video.id);
                  
                  return (
                    <div
                      key={video.id}
                      onClick={() => handleVideoToggle(video.id)}
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: isSelected ? '2px solid #FF0000' : '2px solid transparent',
                        boxShadow: isSelected ? '0 4px 12px rgba(255, 0, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s',
                        position: 'relative',
                      }}
                    >
                      {/* Selection Indicator */}
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: isSelected ? '#FF0000' : 'white',
                        border: isSelected ? 'none' : '2px solid #E5E7EB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      }}>
                        {isSelected && <Check size={20} color="white" />}
                      </div>

                      {/* YouTube Embed */}
                      <div style={{
                        position: 'relative',
                        paddingBottom: '56.25%',
                        height: 0,
                        overflow: 'hidden',
                      }}>
                        <iframe
                          src={`https://www.youtube.com/embed/${video.provider_post_id}?modestbranding=1&rel=0`}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                          }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      {/* Video Info */}
                      <div style={{ padding: '16px' }}>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1F2937',
                          marginBottom: '8px',
                          lineHeight: '1.4',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {video.title}
                        </div>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginBottom: '8px',
                        }}>
                          <Calendar size={14} color="#6B7280" />
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>
                            {new Date(video.published_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                            {' '}
                            {new Date(video.published_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>

                        {video.description && (
                          <div style={{
                            fontSize: '14px',
                            color: '#6B7280',
                            lineHeight: '1.4',
                            marginBottom: '12px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {video.description}
                          </div>
                        )}

                        {/* Stats */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                          paddingTop: '12px',
                          marginTop: '8px',
                          borderTop: '1px solid #F3F4F6',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Eye size={14} color="#6B7280" />
                            <span style={{ fontSize: '12px', color: '#6B7280' }}>
                              {formatViews(video.view_count)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Heart size={14} color="#6B7280" />
                            <span style={{ fontSize: '12px', color: '#6B7280' }}>
                              {formatViews(video.like_count)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <MessageCircle size={14} color="#6B7280" />
                            <span style={{ fontSize: '12px', color: '#6B7280' }}>
                              {formatViews(video.comment_count)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Share2 size={14} color="#6B7280" />
                            <span style={{ fontSize: '12px', color: '#6B7280' }}>0</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default YouTubeVideoPickerModal;
